/* eslint-disable prettier/prettier */
import SHA1 from "crypto-js/sha1";
import _ from "lodash";
import axios from "@/core/services/axios";
import router from "@/router/clean";
import AppConfig from "@/config/app.config";
import { checkTokenExpiration } from "asokoinsight-tokens/src/helpers";

import {
  SET_CURRENT_USER,
  CLEAR_USER,
  SET_SEARCHED_USER,
  CLEAN_SEARCHED_USER,
} from "./mutation-types";
import { adminUser } from "../../../Constants/userRoles";

const state = {
  currentUser: {},
  searchedUsers: [],
};

const getPlatformId = (rootGetters) => rootGetters["platform/platformId"];

const mutations = {
  [SET_CURRENT_USER](state, user) {
    state.currentUser = user;
  },
  [CLEAR_USER](state) {
    state.currentUser = {};
  },
  [SET_SEARCHED_USER](state, user) {
    state.searchedUsers = user;
  },
  [CLEAN_SEARCHED_USER](state) {
    state.searchedUsers = [];
  },
};

const sessionHandler = ({ commit, dispatch }, data) => {
  const displayName = data.displayName || data.username;
  const publicRoles = data.roles;
  const email = data.primaryEmail;
  let firstName = _.get(data, "data.firstName");
  let lastName = _.get(data, "data.lastName");
  const [givenName, familyName] = (displayName || "").split(" ");
  if (!firstName) {
    firstName = givenName || "<not specified>";
  }
  if (!lastName) {
    lastName = familyName || "<not specified>";
  }

  const user = {
    name: displayName,
    firstName,
    lastName,
    email,
    accessToken: data.token.accessToken,
    intercomHmac: data.data.intercomHmac,
    userId: data.id,
    isActiveSubscriber: false,
    roles: publicRoles,
    interestId: data.data.interestId,
  };

  commit(SET_CURRENT_USER, user);
  dispatch("tokens/setTokensData", data.token, { root: true });
};

const actions = {
  checkAndRefresh({ dispatch }) {
    const useBrowserAuth = AppConfig.authSessionMode === "browser";
    const refreshExpiresAt = localStorage.getItem("refreshExpiresAt");
    const refreshToken = localStorage.getItem("refreshToken");
    let isExpired = false;
    if (refreshExpiresAt) {
      isExpired = checkTokenExpiration(refreshExpiresAt);
    }

    const canRefresh = useBrowserAuth
      ? Boolean(refreshToken && (!refreshExpiresAt || !isExpired))
      : Boolean(refreshExpiresAt && !isExpired);

    if (canRefresh) {
      return dispatch("tokens/refreshAccessToken", null, { root: true })
        .catch((err) => {
          console.log(err);
          dispatch("tokens/setTokenChecked", null, { root: true });
        });
    }

    dispatch("tokens/setTokenChecked", null, { root: true });
    return Promise.resolve();
  },
  login({ commit, dispatch, rootGetters }, payload) {
    const platformId = getPlatformId(rootGetters);
    return axios
      .post("/users/sessions?v2", {
        login: payload.email,
        password: payload.password,
        platformId,
        verifyEmail: payload.verifyEmail || false,
      })
      .then((res) => {
        if (!payload.verifyEmail) {
          sessionHandler({ commit, dispatch }, res.data);
        }
        return res.data;
      });
  },
  handleSessionUpdate({ commit, dispatch }, data) {
    sessionHandler({ commit, dispatch }, data);
  },
  handleRefreshFail({ commit }) {
    commit(CLEAR_USER);
    router.push("/");
  },
  logout({ commit, dispatch }) {
    return axios
      .delete("/users/sessions/me")
      .then(() => {
        commit(CLEAR_USER);
        dispatch("tokens/clearTokens", null, { root: true });
      })
      .catch((err) => {
        if (_.get(err, "response.status") === 401) {
          location.reload();
        }
      });
  },
  register({ dispatch, rootGetters }, userData) {
    const platformId = getPlatformId(rootGetters);
    const newAccount = { ...userData };

    const emailHash = SHA1(userData.email).toString();
    newAccount.username = `${userData.firstName}_${emailHash}`;

    newAccount.displayName = `${newAccount.firstName}  ${newAccount.lastName}`;

    newAccount.acceptTerms = !!newAccount.acceptTerms;

    delete newAccount.password_confirmation;

    //start HARDCODE
    newAccount.sector = "Advisory - Management Consulting";
    newAccount.userType = adminUser;
    newAccount.platformId = platformId;
    //end HARDCODE

    const analyticsUtm = _.pickBy(
      userData,
      (val, key) => _.startsWith(key, "utm") && val
    );
    if (!_.isEmpty(analyticsUtm)) {
      newAccount.analyticsUtm = analyticsUtm;
    }

    const emailVerification = rootGetters["platform/isEmailVerificationEnabled"];

    return axios.post("/users/new?v2", newAccount).then(() => {
      if (emailVerification) {
        return { emailVerificationRequired: true };
      }

      return dispatch("login", {
        email: userData.email,
        password: userData.password,
      }).then(() => ({ emailVerificationRequired: false }));
    });
  },
  clearCurrentUser({ commit, dispatch }) {
    commit(CLEAR_USER);
    dispatch("tokens/clearTokens", null, { root: true });
  },
  resetPassword(_, payload) {
    payload.path = `${window.location.origin}/#/new-password/`;
    return axios.post("/users/reset-password", payload);
  },
  searchUser({ commit }, payload) {
    return axios.get(`/users/lookup-external/${payload}`).then((response) => {
      commit(SET_SEARCHED_USER, response.data.items);
    });
  },
  cleanSearch({ commit }) {
    commit(CLEAN_SEARCHED_USER);
  },
};

const getters = {
  currentUser: (state) => state.currentUser,
  isLoggedIn: (state) => !_.isEmpty(state.currentUser),
  userToken: (state) => state.currentUser.accessToken,
  userId: (state) => state.currentUser.userId || "guest",
  userSubmissionId: (state) => state.currentUser.interestId,
  userRoles: (state) => state.currentUser.roles,
  searchedUsers: (state) => state.searchedUsers,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
